import { useCallback, useContext, useEffect, useState } from 'react';
import { Box, Button, Chip, ToggleButton, ToggleButtonGroup, useMediaQuery } from '@mui/material';
import { AddOutlined } from '@mui/icons-material';
import { segmentedToggleSx } from '../../../shared/utils/segmented-toggle-sx';
import { PageOuterContainer } from '../../../root-components/layouts/page-outer-container';
import { PageHeading } from '../../presentation-components/display-elements-components/page-heading';
import { LoadingPage } from '../loading-page';
import { TableDisplay } from '../../presentation-components/miscellaneous-components/table-display';
import { TableActionButtons } from '../../presentation-components/miscellaneous-components/table-action-buttons';
import { IHeadCell } from '../../../shared/interfaces/table-display.model';
import { IconName } from '../../../shared/interfaces/icon-picker.model';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import SurveyAdminService from '../../../shared/services/survey/survey-admin.service';
import { ISurveyQuestion, ISurveyQuestionType } from '../../../shared/services/survey/survey.model';
import { SurveyQuestionDialog } from '../../dialogs/surveys/survey-question-dialog';

import gaiaTheme from '../../../root-components/theme';

const headCells: IHeadCell[] = [
  { id: 'prompt', label: 'Question', numeric: false, sortable: false, includeInMobile: true },
  { id: 'type', label: 'Type', numeric: false, sortable: false, includeInMobile: true },
  { id: 'required', label: 'Required', numeric: false, sortable: false },
  { id: 'calculations', label: 'Calculations', numeric: false, sortable: false },
  { id: 'moreDetails', label: 'Extra details', numeric: false, sortable: false },
  { id: 'options', label: 'Options', numeric: true, sortable: false },
  { id: 'actions', label: '', numeric: false, sortable: false },
];

export const SurveyQuestionsPage = ({ embedded = false }: { embedded?: boolean }) => {
  const surveyAdminService = SurveyAdminService.getInstance();
  const userNotificationsContext = useContext(UserNotificationsContext);
  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('sm'));

  const [tabIndex, setTabIndex] = useState(0);
  const [questions, setQuestions] = useState<ISurveyQuestion[]>();
  const [archivedQuestions, setArchivedQuestions] = useState<ISurveyQuestion[]>();
  const [questionTypes, setQuestionTypes] = useState<ISurveyQuestionType[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [archivedTotal, setArchivedTotal] = useState(0);
  const [inputPage, setInputPage] = useState(0);
  const [archivedPage, setArchivedPage] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [archivedPerPage, setArchivedPerPage] = useState(10);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState<ISurveyQuestion | undefined>();
  const [selected, setSelected] = useState<string[]>([]);

  const loadTypes = useCallback(async () => {
    try {
      const types = await surveyAdminService.getQuestionTypes();
      setQuestionTypes(types);
    } catch { /* non-critical */ }
  }, []);

  const loadActive = useCallback(async () => {
    setQuestions(undefined);
    try {
      const result = await surveyAdminService.getQuestions({ active: true, page: inputPage, perPage });
      setQuestions(result.rows);
      setTotalRecords(result.count);
    } catch { setQuestions([]); }
  }, [inputPage, perPage]);

  const loadArchived = useCallback(async () => {
    setArchivedQuestions(undefined);
    try {
      const result = await surveyAdminService.getQuestions({ active: false, page: archivedPage, perPage: archivedPerPage });
      setArchivedQuestions(result.rows);
      setArchivedTotal(result.count);
    } catch { setArchivedQuestions([]); }
  }, [archivedPage, archivedPerPage]);

  useEffect(() => { loadTypes(); }, [loadTypes]);
  useEffect(() => { loadActive(); }, [loadActive]);
  useEffect(() => { loadArchived(); }, [loadArchived]);

  const reload = () => { loadActive(); loadArchived(); };

  const handleEdit = (question: ISurveyQuestion) => {
    setSelectedQuestion(question);
    setOpenDialog(true);
  };

  const handleArchive = (question: ISurveyQuestion) => {
    userNotificationsContext.openDialog({
      active: true,
      title: 'Archive question',
      message: `Archive "${question.prompt}"? It will no longer appear in question pickers but stays in any question sets already using it.`,
      buttonCopy: 'Archive',
      cancelButtonCopy: 'Cancel',
    }).then(async ok => {
      if (!ok) return;
      try {
        await surveyAdminService.updateQuestion(question.id, { active: false } as any);
        userNotificationsContext.openSnackbar({ open: true, message: 'Question archived', backgroundColor: 'primary.dark' });
        reload();
      } catch {
        userNotificationsContext.openSnackbar({ open: true, message: 'Failed to archive question', backgroundColor: 'text.primary' });
      }
    });
  };

  const handleUnarchive = (question: ISurveyQuestion) => {
    userNotificationsContext.openDialog({
      active: true,
      title: 'Restore question',
      message: `Restore "${question.prompt}"? It will become available to select in question sets again.`,
      buttonCopy: 'Restore',
      cancelButtonCopy: 'Cancel',
    }).then(async ok => {
      if (!ok) return;
      try {
        await surveyAdminService.updateQuestion(question.id, { active: true } as any);
        userNotificationsContext.openSnackbar({ open: true, message: 'Question restored', backgroundColor: 'primary.dark' });
        reload();
      } catch {
        userNotificationsContext.openSnackbar({ open: true, message: 'Failed to restore question', backgroundColor: 'text.primary' });
      }
    });
  };

  const handleDelete = (question: ISurveyQuestion) => {
    userNotificationsContext.openDialog({
      active: true,
      title: 'Delete question',
      message: `Permanently delete "${question.prompt}"? This cannot be undone.`,
      buttonCopy: 'Delete',
      cancelButtonCopy: 'Cancel',
    }).then(async confirmed => {
      if (!confirmed) return;
      try {
        await surveyAdminService.deleteQuestion(question.id);
        userNotificationsContext.openSnackbar({ open: true, message: 'Question deleted', backgroundColor: 'primary.dark' });
        reload();
      } catch (err: any) {
        const data = err?.response?.data;
        const msg = data?.message ?? 'Failed to delete question';
        // A question with responses cannot be deleted; offer the archive path in the
        // same breath instead of an error snackbar followed by a second dialog.
        if (data?.hasResponses) {
          userNotificationsContext.openDialog({
            active: true,
            title: 'This question cannot be deleted',
            message: `${msg} Archive it instead? Archived questions keep their recorded answers but stop appearing in question pickers.`,
            buttonCopy: 'Archive',
            cancelButtonCopy: 'Cancel',
          }).then(async ok => {
            if (ok) handleArchive(question);
          });
        } else {
          userNotificationsContext.openSnackbar({ open: true, message: msg, backgroundColor: 'text.primary' });
        }
      }
    });
  };

  // Mirrors the eligibility rule the calculation dialogs apply, so the trap is
  // visible in the library instead of surfacing later as a greyed-out question.
  const calculationsCell = (q: ISurveyQuestion) => {
    const code = q.surveyQuestionType?.code;
    const eligible =
      code === 'NUMBER'
      || ((code === 'TEXT_SINGLE_LINE' || code === 'TEXT_MULTI_LINE') && q.enforceNumericAnswer === true)
      || code === 'DATE'
      || ((code === 'SELECT_SINGLE' || code === 'SELECT_MULTI' || code === 'RADIO' || code === 'CHECKBOX_GROUP')
        && (q.surveyQuestionOptions?.length ?? 0) > 0
        && (q.surveyQuestionOptions ?? []).every(o => o.numericValue != null && Number.isFinite(Number(o.numericValue))));
    return eligible
      ? <Chip label='Usable' color='info' variant='outlined' size='small' />
      : '—';
  };

  const makeRows = (list: ISurveyQuestion[], archived: boolean) =>
    list.map(q => ({
      id: q.id,
      prompt: q.prompt,
      type: <Chip label={q.surveyQuestionType?.label ?? q.surveyQuestionTypeId} size='small' variant='outlined' />,
      required: q.isRequired ? <Chip label='Yes' color='primary' size='small' /> : '—',
      calculations: calculationsCell(q),
      moreDetails: q.collectMoreDetails ? <Chip label='Yes' color='secondary' variant='outlined' size='small' /> : '—',
      options: q.surveyQuestionOptions?.length ?? 0,
      actions: archived ? (
        <TableActionButtons
          id={q.id}
          includeEdit={false}
          includeDelete
          onDelete={() => handleDelete(q)}
          additionalMenuItems={[{ label: 'Restore', onClick: () => handleUnarchive(q) }]}
        />
      ) : (
        <TableActionButtons
          id={q.id}
          includeEdit
          includeDelete={false}
          onEdit={() => handleEdit(q)}
          additionalMenuItems={[{ label: 'Archive', onClick: () => handleArchive(q) }]}
        />
      ),
    }));

  const Wrapper = embedded ? Box : PageOuterContainer;
  const openCreate = () => { setSelectedQuestion(undefined); setOpenDialog(true); };

  return (
    <Wrapper>
      {!embedded && (
        <PageHeading
          title='Question Library'
          includeActionButton
          actionButtonText='New Question'
          actionButtonIcon={IconName.Add}
          actionButtonVariant='contained'
          actionButtonHandler={openCreate}
        />
      )}
      {/* One toolbar row directly above the table: the status filter it belongs to on
          the left, the create action on the right. */}
      <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <ToggleButtonGroup
          size='small'
          exclusive
          value={tabIndex}
          onChange={(_, v) => { if (v !== null) setTabIndex(v); }}
          sx={segmentedToggleSx}
        >
          <ToggleButton value={0}>Active</ToggleButton>
          <ToggleButton value={1}>{`Archived${archivedTotal > 0 ? ` (${archivedTotal})` : ''}`}</ToggleButton>
        </ToggleButtonGroup>
        {embedded && (
          <Button variant='contained' startIcon={<AddOutlined />} onClick={openCreate} sx={{ whiteSpace: 'nowrap' }}>
            New Question
          </Button>
        )}
      </Box>
      {tabIndex === 0 && (
        <LoadingPage pageReady={!!questions}>
          <TableDisplay
            rows={makeRows(questions ?? [], false)}
            headCells={headCells}
            isMobileMode={isMobileMode}
            selected={selected}
            setSelected={setSelected}
            totalRecords={totalRecords}
            inputPage={inputPage}
            setInputPage={setInputPage}
            perPage={perPage}
            setPerPage={setPerPage}
          />
        </LoadingPage>
      )}
      {tabIndex === 1 && (
        <LoadingPage pageReady={!!archivedQuestions}>
          <TableDisplay
            rows={makeRows(archivedQuestions ?? [], true)}
            headCells={headCells}
            isMobileMode={isMobileMode}
            selected={selected}
            setSelected={setSelected}
            totalRecords={archivedTotal}
            inputPage={archivedPage}
            setInputPage={setArchivedPage}
            perPage={archivedPerPage}
            setPerPage={setArchivedPerPage}
          />
        </LoadingPage>
      )}
      <SurveyQuestionDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        successHandler={reload}
        questionTypes={questionTypes}
        existingQuestion={selectedQuestion}
      />
    </Wrapper>
  );
};
